import React, { useCallback } from 'react';
import { 
    useForm, 
    useFieldArray, 
    FieldValues, 
    UseFormReturn, 
    UseFormProps, 
    UseFieldArrayReturn 
} from 'react-hook-form';
import FormInput, { FormInputProps } from './FormInput';
import { yupResolver } from '@hookform/resolvers/yup';

export type OmitProps = 'register' | 'errors' | 'touchedFields' | 'trigger';

interface UseTypedFormOptions<T extends FieldValues> extends UseFormProps<T> {}

function useTypedForm<T extends FieldValues>(options?: UseTypedFormOptions<T>) {
    const form = useForm<T>(options);

    // Store field array instances dynamically
    const fieldArrayHelpers: Record<string, UseFieldArrayReturn<T, any>> = {};

    // Function to register field arrays dynamically
    const registerFieldArray = (name: keyof T) => {
        if (!fieldArrayHelpers[name as string]) {
            /* eslint-disable */
            fieldArrayHelpers[name as string] = useFieldArray({ control: form.control, name: name as any });
        }
        return fieldArrayHelpers[name as string];
    };

    const TypedFormInput = useCallback<React.FC<Omit<FormInputProps<T>, OmitProps>>>(
        ({ name, ...props }) => (
            <FormInput
                {...props}
                name={name}
                register={form.register}
                errors={form.formState.errors}
                touchedFields={form.formState.touchedFields}
                trigger={form.trigger}
                watch={form.watch} // ✅ Pass down watch
            />
        ),
        [form.register, form.formState.errors, form.watch] // ✅ Ensure re-renders when needed
    );

    return { ...form, FormInput: TypedFormInput, registerFieldArray };
}

export default useTypedForm;
